import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import styles from "../../../styles/styles";
import ProductCard from "../ProductCard/ProductCard";

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** New Additions: active listings from the last 7 days in timeline order (newest first). */
const getNewAdditionsProducts = (allProducts = [], limit) => {
  const now = Date.now();
  const freshProducts = (allProducts || []).filter((product) => {
    const createdAt = new Date(product.createdAt || 0).getTime();
    if (!createdAt || Number.isNaN(createdAt)) return false;
    return now - createdAt <= ONE_WEEK_MS;
  });

  freshProducts.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  const cap = typeof limit === "number" && limit > 0 ? limit : 12;
  return freshProducts.slice(0, cap);
};

const NewAdditions = ({ config = {} }) => {
  const { allProducts } = useSelector((state) => state.products);
  const sliderRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoplayEnabled = config.autoplay === true;
  const autoplaySpeed = config.autoplaySpeed || 3000;
  const showArrows = config.showArrows !== false;
  const productsToShow = Math.min(8, Math.max(1, Number(config.productsToShow) || 5));
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  const visibleCards = isMobile ? 2.2 : productsToShow;
  const gap = 24;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const productsToDisplay = useMemo(
    () => getNewAdditionsProducts(allProducts, config.limit),
    [allProducts, config.limit]
  );

  const scrollToIndex = useCallback(
    (index) => {
      if (!sliderRef.current || !productsToDisplay.length) return;
      const container = sliderRef.current;
      const card = container.querySelector("[data-product-card]");
      if (!card) return;
      const cardWidth = card.offsetWidth + gap;
      const maxIndex = Math.max(0, productsToDisplay.length - Math.ceil(visibleCards));
      const clamped = Math.max(0, Math.min(index, maxIndex));
      container.scrollTo({ left: clamped * cardWidth, behavior: "smooth" });
      setCurrentIndex(clamped);
    },
    [productsToDisplay.length, visibleCards, gap]
  );

  const startAutoplay = useCallback(() => {
    if (!autoplayEnabled || !productsToDisplay.length || productsToDisplay.length <= Math.ceil(visibleCards)) return;
    return setInterval(() => {
      setCurrentIndex((prev) => {
        const maxIndex = Math.max(0, productsToDisplay.length - Math.ceil(visibleCards));
        const next = prev >= maxIndex ? 0 : prev + 1;
        if (sliderRef.current) {
          const container = sliderRef.current;
          const card = container.querySelector("[data-product-card]");
          if (card) {
            const cardWidth = card.offsetWidth + gap;
            container.scrollTo({ left: next * cardWidth, behavior: "smooth" });
          }
        }
        return next;
      });
    }, autoplaySpeed);
  }, [autoplayEnabled, autoplaySpeed, productsToDisplay.length, visibleCards, gap]);

  useEffect(() => {
    if (!autoplayEnabled || productsToDisplay.length <= Math.ceil(visibleCards)) return;
    const id = startAutoplay();
    return () => {
      if (id) clearInterval(id);
    };
  }, [autoplayEnabled, startAutoplay, productsToDisplay.length, visibleCards]);

  const handleScroll = useCallback(() => {
    if (!sliderRef.current) return;
    const container = sliderRef.current;
    const card = container.querySelector("[data-product-card]");
    if (!card) return;
    const cardWidth = card.offsetWidth + gap;
    const index = Math.round(container.scrollLeft / cardWidth);
    setCurrentIndex(Math.max(0, Math.min(index, productsToDisplay.length - 1)));
  }, [productsToDisplay.length, gap]);

  const handlePrev = () => scrollToIndex(currentIndex - 1);
  const handleNext = () => scrollToIndex(currentIndex + 1);

  const viewAllLink = config.viewAllLink || "/products";
  const buttonText = config.buttonText || "View All";
  const showNav = productsToDisplay.length > Math.ceil(visibleCards);
  const hasProducts = productsToDisplay.length > 0;

  return (
    <div className="mb-12">
      <div className={`${styles.section}`}>
        <div className="flex justify-between items-center pb-5">
          <div>
            <h1 className={`${styles.heading} pb-0`}>
              {config.heading || "New Additions"}
            </h1>
            {config.subheading && (
              <p className="text-sm text-gray-500 mt-1">{config.subheading}</p>
            )}
          </div>
          {viewAllLink && (
            <Link to={viewAllLink}>
              <div className={`${styles.button} text-[#fff]`}>
                {buttonText}
              </div>
            </Link>
          )}
        </div>

        {hasProducts ? (
          <div className="relative">
            <div
              ref={sliderRef}
              onScroll={handleScroll}
              className="w-full overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <div className="flex gap-6" style={{ gap: `${gap}px` }}>
                {productsToDisplay.map((product) => (
                  <div
                    key={product._id}
                    data-product-card
                    className="flex-shrink-0 snap-start home-product-card"
                    style={{
                      width: `calc((100% - ${(visibleCards - 1) * gap}px) / ${visibleCards})`,
                      minWidth: `calc((100% - ${(visibleCards - 1) * gap}px) / ${visibleCards})`,
                    }}
                  >
                    <ProductCard data={product} />
                  </div>
                ))}
              </div>
            </div>

            {showNav && showArrows && (
              <>
                <button
                  type="button"
                  onClick={handlePrev}
                  className="home-prod-arrow-left absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg border border-gray-200 transition-all hover:scale-110"
                  aria-label="Previous"
                >
                  <IoIosArrowBack size={24} />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="home-prod-arrow-right absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg border border-gray-200 transition-all hover:scale-110"
                  aria-label="Next"
                >
                  <IoIosArrowForward size={24} />
                </button>
              </>
            )}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">
            No new listings in the last 7 days.
          </p>
        )}
      </div>
    </div>
  );
};

export default NewAdditions;
